import {
  always,
  applySpec,
  compose,
  cond,
  defaultTo,
  equals,
  filter,
  findIndex,
  flatten,
  head,
  ifElse,
  isNil,
  length,
  map,
  pick,
  pluck,
  prop,
  propEq,
  propOr,
  replace,
  sum,
  T
} from 'ramda';
import { formatCurrency } from '@@viewHelpers/formatCurrency.mjs';
import useDownloadDocumentPricingIndividualHealthMutation from './useDownloadDocumentPricingIndividualHealthMutationQuery.js';
import React, { useState } from 'react';

const usePricingsDisplayer = ({
  currentPricings,
  setValue,
  maxNumberOfProducts,
  setShowDocumentError,
  visiblePackIds,
  currentBeneficiaryIndex,
  selectedCommissionRate,
  packs,
  viewMode
}) => {
  const [displaySupplements, setDisplaySupplements] = React.useState(false);

  const [displayError, setDisplayError] = useState(false);

  const [downloadNotice, setDownloadNotice] = useState({
    downloading: false,
    moduleCode: ''
  });

  const formatCurrencyWithoutSpace = compose(replace(/\s/g, ''), formatCurrency);

  const { onFormSubmit: downloadMutation } = useDownloadDocumentPricingIndividualHealthMutation();

  const currentSelectedPricings =
    viewMode === 'CUSTOMER_GRANTEES'
      ? compose(filter(prop('isPreSelected')), flatten, pluck('pricings'))(packs)
      : filter(prop('isPreSelected'))(currentPricings);

  const downloadDocuments = (code) => {
    setDownloadNotice({
      downloading: true,
      moduleCode: code
    });
    setShowDocumentError(false);

    downloadMutation({ code }, async (ok, error, { url }) => {
      setDownloadNotice({
        downloading: false,
        moduleCode: code
      });
      if (ok && !error) {
        window.open(url);
      } else {
        setShowDocumentError(true);
      }
    });
  };

  const toggleIsSelected = (pricing) => {
    const pricingIndex = findIndex(propEq('moduleCode', pricing.moduleCode), currentPricings);
    if (equals(maxNumberOfProducts, 1)) {
      const pricingsToUpdate = map((pricingToUpdate) => {
        pricingToUpdate.isPreSelected = propEq('moduleCode', pricing.moduleCode, pricingToUpdate);

        return pricingToUpdate;
      })(currentPricings);
      return setValue(`packs.${currentBeneficiaryIndex}.pricings`, pricingsToUpdate);
    }

    if (!pricing.isPreSelected) {
      const maxProductsSelected = compose(
        equals(maxNumberOfProducts),
        length,
        filter(prop('isPreSelected'))
      )(currentPricings);

      if (maxProductsSelected) return setDisplayError(maxProductsSelected);
    }

    setDisplayError(false);
    setValue(
      `packs.${currentBeneficiaryIndex}.pricings.${pricingIndex}.isPreSelected`,
      !pricing.isPreSelected
    );
  };

  const dynamicColumns = cond([
    [equals(1), always('md:grid-cols-1!')],
    [equals(2), always('md:grid-cols-2!')],
    [equals(3), always('md:grid-cols-3!')],
    [equals(4), always('md:grid-cols-4!')],
    [equals(5), always('md:grid-cols-5!')],
    [equals(6), always('md:grid-cols-6!')],
    [T, always(`md:grid-cols-${length(currentPricings)}!`)]
  ])(length(visiblePackIds));

  const getPriceByCommission = compose(
    ifElse(
      always(viewMode === 'CUSTOMER'),
      compose(
        applySpec({
          initialPrice: compose(propOr(0, 'initialPrice'), head),
          defaultPrice: compose(sum, map(propOr(0, 'defaultPrice')))
        })
      ),
      compose(pick(['initialPrice', 'defaultPrice']), defaultTo({}), head)
    ),
    defaultTo([]),
    filter(propEq('commission', selectedCommissionRate)),
    propOr([], 'prices')
  );
  const getTotalPrice = (pricing) => {
    if (isNil(pricing)) return 0;
    const basePrice = getPriceByCommission(pricing).defaultPrice || 0;

    const supplementaryPrices = compose(
      sum,
      map(compose(propOr(0, 'defaultPrice'), getPriceByCommission)),
      filter(prop('isPreSelected'))
    )(pricing.supplementaryCovers || []);

    return basePrice + supplementaryPrices;

  };

  const totalSelectedPricings = compose(sum, map(getTotalPrice))(currentSelectedPricings);

  const round = (value) => Math.round(value * 100) / 100;

  return {
    displaySupplements,
    setDisplaySupplements,
    displayError,
    downloadNotice,
    formatCurrencyWithoutSpace,
    downloadDocuments,
    toggleIsSelected,
    dynamicColumns,
    getPriceByCommission,
    getTotalPrice,
    totalSelectedPricings,
    round
  };
};

export default usePricingsDisplayer;
